import PhoenixDocCard from 'components/base/PhoenixDocCard';
import React, { useEffect, useState } from 'react';
import { RoleService, Role } from 'services/role.service';
import AuthService from 'services/auth.service';
import { getAccount, OneAccount } from 'services/account.service';
import { useFormik } from 'formik';
import FormResultMessage from './FormResultMessage';

type MessageType = 'successupdate' | 'errorupdate';

const UpdateSAMLUserForm: React.FC<{
  accountId: number;
  onClose: () => void;
  onUpdate?: () => void;
}> = ({ accountId, onClose, onUpdate }) => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleNames, setSelectedRoleNames] = useState<string>('');
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<MessageType>('successupdate');
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<OneAccount | null>(null);

  useEffect(() => {
    const fetchRoles = async () => {
      const rolesData = await RoleService.getAllRoles();
      setRoles(rolesData);
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getAccount(accountId);
        setCurrentUser(userData);
        setSelectedRoleNames(userData.roles);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [accountId]);

  const formik = useFormik({
    initialValues: {
      roles: ''
    },
    onSubmit: async () => {
      try {
        // eslint-disable-next-line
        const rolesArray = selectedRoleNames.split(',').map(role => role.trim());
        await AuthService.updateUserRoles(accountId, rolesArray);
        setMessage('User roles have been updated');
        setMessageType('successupdate');
        if (onUpdate) {
          onUpdate();
        }
      } catch (error) {
        setMessage('Error updating roles: ' + error);
        setMessageType('errorupdate');
      }
      setModalVisible(true);
    }
  });

  const handleCheckboxChange = (role: Role) => {
    const trimmedRoleName = role.rolename.trim();
    setSelectedRoleNames(prevSelectedRoleNames => {
      const rolesArray = prevSelectedRoleNames.split(',').map(r => r.trim());
      if (rolesArray.includes(trimmedRoleName)) {
        return rolesArray.filter(name => name !== trimmedRoleName).join(',');
      } else {
        return [...rolesArray, trimmedRoleName].join(',');
      }
    });
  };

  useEffect(() => {
    formik.setFieldValue('roles', selectedRoleNames);
  }, [selectedRoleNames]);

  const closeModal = () => {
    setModalVisible(false);
    onClose();
  };

  // Mostrar información del usuario (no editable)
  const UserInfo = () => (
    <>
      <div className="mx-6 mt-4 px-1 pb-1 fs-9">
        On SAML accounts, only authorization level can be updated. However, if
        roles are defined at the Identity Provider, they will be overridden
        during the next login.
      </div>
      <div className="mx-6 p-3 mb-3 rounded border bg-body fs-9">
        <div className="row">
          <div className="col-2 text-muted">Name:</div>
          <div className="col-9">
            {currentUser?.name} {currentUser?.lastname}
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-muted">Email:</div>
          <div className="col-9">{currentUser?.email}</div>
        </div>
        <div className="row">
          <div className="col-2 text-muted">Username:</div>
          <div className="col-9">{currentUser?.username}</div>
        </div>
        <div className="row">
          <div className="col-2 text-muted">Auth Type:</div>
          <div className="col-9">
            <span className="badge bg-success">
              {currentUser?.auth_type === 'saml-azure'
                ? 'Azure AD'
                : 'ManageEngine ADS'}
            </span>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div>
      <UserInfo />
      <form className="mb-1" onSubmit={formik.handleSubmit}>
        <label className="form-label p-8 pt-3 pb-0">
          Select account authorization level
        </label>
        <div className="p-6 pt-0 pb-2">
          <PhoenixDocCard className={'border'}>
            <PhoenixDocCard.Body className="p-0 pt-0 pb-0">
              {roles.map(role => (
                <div key={role.id} className="form-check form-switch p-0 mb-0">
                  <label className="form-check-label p-4 pt-0 pb-0 mb-0">
                    <input
                      type="checkbox"
                      value={role.rolename}
                      checked={selectedRoleNames.includes(role.rolename)}
                      onChange={() => handleCheckboxChange(role)}
                      className="form-check-input"
                      title={role.description}
                      aria-label={role.description}
                    />
                    {role.description}
                  </label>
                </div>
              ))}
            </PhoenixDocCard.Body>
          </PhoenixDocCard>
        </div>
        <hr className="p-0 pt-3 mb-0" />
        <div className="p-3 pt-0 text-end">
          <button
            type="button"
            className="btn btn-secondary btn-block p-2 px-4 m-1 mt-0"
            onClick={closeModal}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary btn-block p-2 px-4 m-1 mt-0"
          >
            Update Roles
          </button>
        </div>
      </form>
      <FormResultMessage
        show={modalVisible}
        message={message}
        messageType={messageType}
        logout={false}
        onClose={closeModal}
      />
    </div>
  );
};

export default UpdateSAMLUserForm;
